import { makeAutoObservable, reaction, runInAction } from "mobx";
import type RootStore from "./RootStore";
import type { ServiceStoreType } from "../types/ServiceStoreTypes";
import {
  SUPPORT_REASON_TEXT,
  currentHolding,
  subscribeBrowser,
  supportLevel,
  unsubscribeBrowser,
} from "../push/webPush";
import type { BrowserHolding } from "../push/webPush";
import { swallowedFindMessage } from "../views/devops/shared/swallowedFind";
import { swallowedSessionEnded } from "../shared/loadOrError";
import { currentUserIdOf } from "../auth/userId";
import {
  clearDeviceOwner,
  readDeviceOwner,
  readLastSignedInUser,
  writeDeviceOwner,
  writeLastSignedInUser,
} from "../push/deviceOwner";
import { isSessionEnded, sessionMark } from "./sessionGuard";

/**
 * EVERY LEG, IN ONE PLACE, BECAUSE FIVE RECORDS ARE KEYED BY IT.
 *
 * `legs`, `saving`, `savingSeq`, `legWrites` and the settings page's `LEG_COPY`
 * are all `Record<PushLeg, …>`, and until this list existed each was a
 * hand-written literal. Adding `email` meant remembering all five; the type
 * would have caught a missed one, but only because every one of them is
 * annotated — and `reset()` rebuilds two of them from scratch, where a stale
 * literal is a leg silently reset to nothing. So the list is the definition and
 * the records are built from it: a sixth leg is one entry here.
 */
export const PUSH_LEGS = ["push", "gantry", "email"] as const;

export type PushLeg = (typeof PUSH_LEGS)[number];

/**
 * One value per leg. Takes a FACTORY, not a value: `perLeg([])` would hand
 * every leg the same array, so pushing a kind onto one leg would push it onto
 * all of them.
 */
const perLeg = <T,>(make: () => T): Record<PushLeg, T> =>
  Object.fromEntries(PUSH_LEGS.map((leg) => [leg, make()])) as Record<
    PushLeg,
    T
  >;

/**
 * Whose this browser's push subscription is.
 *   "none"     — the browser ANSWERED, and holds none (or held one that was
 *                not this person's and it is GONE)
 *   "mine"     — the server confirmed the endpoint is the signed-in person's row, or this store just registered it
 *   "unknown"  — the browser holds one, but the server did not say whose. KEPT
 *                (unanswered is not "not yours"), never shown as on, asked again next load.
 *   "unchecked"— nothing is known about what this browser holds, because
 *                nothing has been established: the question has not been put
 *                yet (the value this store STARTS and RESETS in), or it was
 *                put and `currentSubscription()` rejected, or it was put and
 *                never settled — AND no earlier answer stands. Not the
 *                endpoint, not whether there is one. Like "stranded" and
 *                "unknown" this must never be reported as "none" — "this
 *                browser holds nothing" is a sentence nobody has earned the
 *                right to say. Never shown as on; asked again next load.
 *
 *                Being the absence of an answer, it is also never written OVER
 *                one: an unanswered question leaves whatever was established
 *                standing (`establishedOwnership`). The single thing that
 *                degrades TO it is a "mine" belonging to somebody else.
 *   "stranded" — the browser holds one this store has no basis to call the
 *                signed-in person's — the server answered "not yours", or the
 *                local record names somebody else, or HQ refused to register
 *                it, or the account changed while it was being made — and the
 *                browser would not let go of it: `unsubscribeBrowser()`
 *                rejected. The subscription is still live and still VALID, so
 *                nothing 404s and nothing is pruned server-side either. This
 *                is the state that must never be reported as "none": that
 *                would tell the person this browser holds nothing while
 *                somebody else's push is still arriving on it.
 */
/**
 * WHAT HAPPENED TO THE SUBSCRIPTION THIS STORE ASKED THE BROWSER TO LET GO OF.
 *
 * Three, matching the three `unsubscribeBrowser` actually distinguishes —
 * because a funnel that carries fewer answers than its callee produces is the
 * fail-open this whole file keeps removing, one layer at a time:
 *
 *   released — the browser let go of the endpoint that was named, and named it
 *              back. The only outcome that earns "this browser holds nothing".
 *   not-held — the endpoint named is NOT on this browser: it holds nothing, or
 *              it holds a DIFFERENT subscription which was deliberately left
 *              alone (it was no part of the decision). Nothing was released,
 *              and nothing has been established about what is there now.
 *   refused  — the browser would not let go: `unsubscribe()` rejected, or the
 *              worker never answered inside the deadline. The subscription is
 *              still HELD and still VALID, so nothing 404s and nothing is
 *              pruned server-side either.
 */
type ReleaseOutcome =
  | { kind: "released"; endpoint: string }
  | { kind: "not-held" }
  | { kind: "refused"; error: Error };

export type DeviceOwnership =
  | "none"
  | "mine"
  | "unknown"
  | "unchecked"
  | "stranded";

/**
 * WHAT THIS BROWSER SAYS IT IS HOLDING — three answers, not two, for the same
 * reason `deviceOwner` reports three about the local record. The type and the
 * function that decides it both live in `push/webPush.ts`, because deciding
 * WHICH of the three it is needs what only that layer knows.
 *
 *   held        — the browser answered and named the endpoint it holds
 *   none        — the browser answered, and holds nothing
 *   unavailable — the browser was not asked, or could not answer.
 *                 `currentHolding()` returns above `getSubscription()` for
 *                 every `supportLevel()` blocker, and both
 *                 `navigator.serviceWorker.ready` and `getSubscription()` can
 *                 reject; a subscription the browser returns without its
 *                 endpoint or keys throws in `toJson`. None of that is evidence
 *                 about what is on the device — the subscription, if there is
 *                 one, is untouched and still delivering to whoever's row it is.
 *
 * Collapsing `unavailable` into `none` is the fail-open this type exists to
 * remove: it lets a question nobody put be read as the answer "there is nothing
 * here". This store held the type and could not populate it — the function it
 * asked answered `null` to "holds nothing" and to "could not be asked" alike —
 * which is why the third answer is now made where it is known.
 */

/**
 * HOW LONG A BROWSER GETS TO ANSWER.
 *
 * Both `webPush` calls this store makes without a person in the loop —
 * `currentSubscription()` and `unsubscribeBrowser()` — first await
 * `navigator.serviceWorker.ready`, which on a worker that never activates does
 * not settle at all. Unbounded, that is not a slow page: it is a page that
 * never finishes loading, showing no switches, no device list and no error,
 * with the button that would ask again disabled because it is still "loading".
 *
 * Silence past the deadline is read as the failure it is and never as an
 * answer: the read becomes "could not be asked", and the drop becomes
 * "refused" — the reading that keeps the subscription and the local record
 * rather than the one that forgets both.
 *
 * `subscribeBrowser()` is deliberately NOT bounded by this. It waits on
 * `Notification.requestPermission()`, which is a person deciding, and they are
 * allowed to take as long as they like.
 */
const BROWSER_ANSWER_DEADLINE_MS = 10_000;

const withinDeadline = <T>(work: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("The browser did not answer in time.")),
      ms,
    );
    void work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err as Error);
      },
    );
  });

/** Ask the browser what it holds, keeping "could not ask" separate from "nothing". */
const askBrowserWhatItHolds = async (): Promise<BrowserHolding> => {
  try {
    const holding = await withinDeadline(
      currentHolding(),
      BROWSER_ANSWER_DEADLINE_MS,
    );
    // An answer with no endpoint in it is not an answer about an endpoint.
    return holding.kind === "held" && !holding.endpoint
      ? { kind: "unavailable", reason: "ask-failed" }
      : holding;
  } catch {
    return { kind: "unavailable", reason: "ask-failed" };
  }
};

/**
 * Issue one request so that NOTHING about it can reach the request beside it —
 * neither the rejection it settles with (that is `allSettled`'s job) nor a
 * synchronous throw while it is being issued, which would otherwise stop the
 * second request from being made at all.
 */
const ask = async (request: () => unknown): Promise<unknown> => await request();

/**
 * A FIELD THE SERVER LEFT OUT IS NOT AN ANSWER ABOUT THAT FIELD.
 *
 * The wire is not the declared type. `?? []` read a missing `legs.push` as
 * "every push kind is off" and a missing `kinds` as "there is nothing that can
 * push" — both of which render as an ordinary, editable, all-off form, and the
 * person's next click saves it for real. `null` here means "not answered", and
 * every caller treats that as the failed read it is. An EMPTY list is a real
 * answer and passes through.
 *
 * THE SENTENCE IS ABOUT EVERY FIELD, NOT EVERY LIST. This helper is only the
 * shape the rule takes for the three lists; the subscriptions answer's own
 * `configured` and `publicKey` are checked where they are read, for the same
 * reason and against the same harm — see `load()`. The one deliberate
 * exception is `subscriptions`, and it is argued at the line that takes it:
 * nothing is ever WRITTEN from `devices`, so a list the server left out can
 * only offer fewer actions than it should, never a wrong one.
 */
const asList = <T>(value: unknown): T[] | null =>
  Array.isArray(value) ? (value as T[]) : null;

/**
 * THE SAME RULE, FOR THE FIELD INSIDE EACH KIND.
 *
 * `asList` asks whether the server answered about `kinds` at all. This asks the
 * question one level down, because a kind's `legs` decides which cards it is
 * drawn on and therefore what the master switch will post: an option that
 * arrived without it is not an answer about where that kind may go, and a
 * default in its place is a guess the next click writes to the database. There
 * is no safe guess available — every leg is the bug this field was added to
 * fix, and no leg silently hides a real kind — so a payload missing it is
 * treated exactly as a missing `kinds` is, and `load()` reports the read as
 * failed rather than rendering a form built on it.
 *
 * An UNKNOWN leg name is different and is merely dropped: it means the API
 * offers a leg this build cannot draw, which is an ordinary deploy ordering and
 * not a failure.
 */
const asKindOptions = (value: unknown): PushKindOption[] | null => {
  const list = asList<unknown>(value);
  if (!list) return null;
  const known = PUSH_LEGS as readonly string[];
  const options: PushKindOption[] = [];
  for (const raw of list) {
    const k = raw as Partial<PushKindOption> | null | undefined;
    const legs = asList<unknown>(k?.legs);
    if (typeof k?.kind !== "string" || !legs) return null;
    options.push({
      kind: k.kind,
      label: typeof k.label === "string" ? k.label : k.kind,
      description: typeof k.description === "string" ? k.description : "",
      legs: legs.filter((leg): leg is PushLeg => known.includes(leg as string)),
    });
  }
  return options;
};

/**
 * THE ROW IS ALREADY GONE — which is the outcome the caller asked for, not a
 * failure to reach it.
 *
 * `push-subscriptions.remove` throws `NotFound('No such device on your
 * account')` when nothing matches the id or the endpoint; it never answers with
 * an empty list, which is what both remove sites here used to assume in a
 * comment. A row can be gone before the click lands for entirely ordinary
 * reasons: HQ's own 404/410 sweep pruned it after the push service rejected the
 * endpoint, another tab or another device removed it, or this is the second
 * press of the same bin. Read as a failure, that told the person the exact
 * opposite of what is true — "it is still registered", over a row that is not.
 *
 * Recognised however the failure arrives: the ServiceStore swallows it onto
 * `errorOnRemove` and resolves null (`throwOnError` is off for these stores),
 * and a per-call `throwOnError` or a service double throws it. Only a 404 is
 * treated this way — every other failure still means the row may be alive.
 */
const isAlreadyGone = (err: unknown): boolean => {
  const e = err as
    | { code?: unknown; className?: unknown; name?: unknown }
    | null
    | undefined;
  return (
    e?.code === 404 || e?.className === "not-found" || e?.name === "NotFound"
  );
};

/** What `notification-preferences` says about the email leg on this HQ. */
export interface EmailDeliveryState {
  configured: boolean;
  sandbox: boolean;
}

/** Both fields, both booleans, or no answer at all — never half of one. */
export const asEmailDeliveryState = (
  value: unknown,
): EmailDeliveryState | null => {
  const v = value as Partial<EmailDeliveryState> | null | undefined;
  return v != null &&
    typeof v.configured === "boolean" &&
    typeof v.sandbox === "boolean"
    ? { configured: v.configured, sandbox: v.sandbox }
    : null;
};

export interface PushKindOption {
  kind: string;
  label: string;
  description: string;
  /**
   * The legs whose checkbox is OFFERED for this kind — the API's `legs`, passed
   * through, narrowed to the legs this web build knows about.
   *
   * WITHOUT THIS FIELD THE PAGE WAS WRONG THE MOMENT REPORTS EXISTED. The API's
   * `DELIVERY_KINDS` grew `weekly_report` and `adhoc_report`, both `legs:
   * ['email']`, and this page rendered every kind on every leg card. Ticking a
   * report under "Push to my devices" — or simply flipping that card's master
   * switch, which sends every kind it renders — posted `weekly_report` on the
   * push leg, and `notification-preferences` rejected the whole write with
   * `400 Not a kind that can be delivered`. Nothing saved, on a control that
   * had worked the day before.
   *
   * A leg this build does not know is DROPPED rather than rejected, so the API
   * can add one before the web can show it. A kind left with no known leg
   * therefore renders nowhere — correct, because this build has nowhere honest
   * to put it.
   */
  legs: PushLeg[];
}

export interface PushDevice {
  id: number;
  userAgent: string | null;
  createdAt: string | null;
  lastSeenAt: string | null;
  failedAt: string | null;
  endpointHint: string;
}

/**
 * Settings → Notifications: the per-person opt-in, both legs, and this
 * device's push subscription.
 *
 * Off is the default on every switch, on purpose (plan §0): a notification
 * reaches a phone only when the person turned that kind on AND the leg is on.
 * The bell is never gated here.
 */
class NotificationSettingsStore {
  rootStore: RootStore;

  loading = false;
  loaded = false;
  error: string | null = null;
  /** The kinds switched on, per leg, as the API holds them. */
  legs: Record<PushLeg, string[]> = perLeg<string[]>(() => []);
  kinds: PushKindOption[] = [];
  /** Server: are VAPID keys set at all? null until the first load. */
  pushConfigured: boolean | null = null;
  /**
   * Server: can this HQ hand mail to SendGrid at all (`configured` — the weak,
   * shape-only claim the API documents, never "will arrive"), and is it in
   * sandbox, where SendGrid takes every message and delivers none?
   *
   * null until a load ANSWERS it, and an answer that leaves it out (an older
   * API) changes nothing: the email card then says only what it always said,
   * rather than printing "email is not set up" about an HQ nobody asked.
   */
  emailState: EmailDeliveryState | null = null;
  publicKey: string | null = null;
  devices: PushDevice[] = [];
  /**
   * See `DeviceOwnership`. The switch is green ONLY for "mine".
   *
   * It STARTS — and `reset()` returns it to — "unchecked", the same state the
   * local in `load()` starts in and for the same reason: until the browser has
   * been asked, nothing about this device is known. "none" is an ANSWER ("the
   * browser answered, and holds none"), and asserting it before the question
   * has been put is invisible while the read settles in a tick and is the
   * whole of what a person sees while it does not — `currentSubscription()`
   * awaits `navigator.serviceWorker.ready`, and a worker that never activates
   * never settles it. After a `reset()`, the subscription being reported as
   * nothing is the account that just left.
   */
  thisDeviceOwnership: DeviceOwnership = "unchecked";
  /**
   * The endpoint the browser last NAMED as the one it holds — kept for every
   * ownership but "none" ("mine", "unknown" and "stranded" are all about a
   * subscription that is here), and carried unchanged through an "unchecked"
   * load, which learned nothing about it either way.
   */
  thisDeviceEndpoint: string | null = null;
  /**
   * WHOM the current "mine" was granted to — `undefined` while it is not
   * granted at all, `null` when it was granted with nobody signed in.
   *
   * A confirmation is not a property of the endpoint; it is a property of the
   * (endpoint, person) pair. Without this, the "an unanswered check never
   * DOWNGRADES a confirmation already held" refinement in `load()` would
   * re-assert a "mine" granted for A to whoever is signed in now.
   */
  private confirmedForUserId: number | null | undefined = undefined;
  /**
   * The last signed-in person this store saw, kept ACROSS `reset()` and —
   * through `hq.push.last-user` — across page loads.
   *
   * `reset()` deliberately forgets everything about the account that left —
   * but *that the account changed at all* is worth remembering, because it is
   * evidence that survives a server that will not answer. In memory alone it
   * did NOT survive a browser restart, a reopened tab or a hard refresh, which
   * is the first load of every fresh page and the ordinary shared-browser
   * story: the second drop rule in `load()` was structurally dead there. It is
   * persisted for that reason, and read back through
   * `previousUserOnThisBrowser()`. A browser that refuses storage still has
   * only this session's memory — see the floor stated in `load()`.
   */
  private lastSignedInUserId: number | null = null;
  /**
   * This browser cannot KEEP the local ownership record.
   *
   * Distinct from "there is no record": a readable store holding nothing is
   * evidence (nothing was ever claimed on this browser), while a store that
   * cannot be read or written is the absence of the whole local defence. The
   * page says so in words rather than implying the endpoint is looked after.
   *
   * Set when `readDeviceOwner` answers `unavailable` or `writeDeviceOwner`
   * returns false; cleared when a write lands or a record is read back. A
   * readable-but-empty store leaves it alone: it proves reads work, not
   * writes. (The `hq.push.last-user` marker deliberately does NOT feed this —
   * its absence can only cost a drop that would have happened, never mislead
   * anyone about an endpoint.)
   */
  localEvidenceUnavailable = false;

  /**
   * WHAT IS IN FLIGHT — one flag PER OPERATION, because four of them can be in
   * flight at once and they disable different controls.
   *
   * A single slot named whichever operation set it LAST, and every site cleared
   * it unconditionally when its own work settled. So a gantry save finishing
   * re-enabled the push switches with the push save still in the air (the page
   * computes busy per leg), and the leg saves and the device registration took
   * turns claiming the flag from one another. That is what turned the
   * whole-object assignment in `saveLeg` from a hard race into an ordinary
   * click: the switch that should have been disabled was live.
   *
   * `forget` is the fourth, and it was missing entirely: `forgetDevice` was the
   * one operation on this page that announced nothing at all while it ran. It
   * removes a row and then RE-READS the whole page (`load()`), so for the
   * length of a round trip plus a refresh the bins stayed live — a second press
   * of the same bin, or a press of another one, against a list the reload was
   * about to replace, with no control anywhere disabled and no spinner to say
   * why. That the interleavings happen to come out consistent is the counters'
   * doing, not the page's; the flag is what makes the page say so.
   */
  saving: Record<PushLeg | "device" | "forget", boolean> = {
    ...perLeg(() => false),
    device: false,
    forget: false,
  };
  /**
   * WHO OWNS EACH `saving` SLOT RIGHT NOW — the thing a cleanup has to prove
   * before it is allowed to clear one.
   *
   * A flag per operation was only half the rule. A SLOT is shared by every
   * operation that ever claims it — the same leg pressed twice, an
   * `enableThisDevice` and the `disableThisDevice` that follows it, a second
   * bin — and each of them cleared its slot unconditionally in its `finally`,
   * so a stale operation's completion flipped a SUCCESSOR's flag from true to
   * false with the successor's request still in the air. The controls came
   * back to life mid-flight: the switches during a save the person cannot see,
   * the bins over a list a reload is about to replace. Every stale-answer path
   * in this store is one of these — a save discarded by `stale()`, a
   * registration abandoned by an account change, a `reset()` for somebody else
   * — and all of them run their `finally`.
   *
   * The unconditional clear was not a mistake to be replaced by a gate,
   * though: a slot left set disables the arriving person's controls with
   * nothing in flight to re-enable them, which is the hazard `reset()` and
   * these `finally`s exist for. So the cleanup is neither gated on staleness
   * nor unconditional — it is OWNERSHIP-CHECKED: it clears the slot if and
   * only if nothing has claimed it since, which is exactly "there is nothing
   * in flight in this slot".
   *
   * NEVER RESET TO ZERO, including by `reset()`. These are monotonic, so a
   * token handed out before a sign-out can never equal one handed out after
   * it; put back to 0 they would collide, and a stale cleanup would clear the
   * arriving person's flag again — the precise bug, reintroduced by the tidy.
   */
  private savingSeq: Record<PushLeg | "device" | "forget", number> = {
    ...perLeg(() => 0),
    device: 0,
    forget: 0,
  };
  actionError: string | null = null;

  /**
   * Claim one `saving` slot, and hand back the ONE cleanup entitled to release
   * it. Call both inside a `runInAction`.
   *
   * The returned function is a no-op once anything else has claimed the slot:
   * the flag then describes THAT operation, and it is that operation's to
   * clear. See `savingSeq`.
   */
  private claimSaving(slot: PushLeg | "device" | "forget"): () => void {
    const token = (this.savingSeq[slot] += 1);
    this.saving[slot] = true;
    return () => {
      if (this.savingSeq[slot] !== token) return;
      this.saving[slot] = false;
    };
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // RECONCILE AT SIGN-IN, NOT AT SETTINGS-OPEN. A browser keeps its push
    // subscription across a sign-out, so if the next person to sign in never
    // opened this page, the PREVIOUS account's row stayed live and kept
    // receiving their ticket titles on this device. This store is app-level
    // and outlives every route, so the check happens the moment the signed-in
    // person changes. Signing OUT only clears what is held: the device still
    // belongs to the person who left, and push arriving while nobody is
    // looking at HQ is the point of push.
    reaction(
      () => this.currentUserId(),
      (id, previous) => {
        if (id === previous) return;
        this.reset();
        if (id != null) void this.load();
      },
    );
  }

  private get preferencesService(): ServiceStoreType | null {
    return (
      (this.rootStore
        .notificationPreferencesStore as ServiceStoreType | null) ?? null
    );
  }
  private get subscriptionsService(): ServiceStoreType | null {
    return (
      (this.rootStore.pushSubscriptionsStore as ServiceStoreType | null) ?? null
    );
  }

  /** What this browser can do, in words. `null` means it can. */
  get browserBlocker(): string | null {
    const s = supportLevel();
    return s.ok === false ? SUPPORT_REASON_TEXT[s.reason] : null;
  }

  get thisDeviceSubscribed(): boolean {
    return this.thisDeviceOwnership === "mine";
  }

  /**
   * Held, but not confirmed as this person's. The Settings page says so in
   * words instead of showing a green switch — and instead of an off one,
   * which would suggest there is nothing on this browser to worry about.
   */
  get thisDeviceUnconfirmed(): boolean {
    return this.thisDeviceOwnership === "unknown";
  }

  /**
   * Held, known NOT to be this person's, and still here: the drop was tried
   * and the browser refused it. Reported louder than "unconfirmed" because
   * something is known to be wrong, and never as "off" — see `DeviceOwnership`.
   */
  get thisDeviceStranded(): boolean {
    return this.thisDeviceOwnership === "stranded";
  }

  /**
   * The browser could not be asked what it is holding. Said in words rather
   * than as an Off switch, which would answer a question nobody managed to
   * ask — and which, on a shared browser, would be the previous account's
   * subscription reported as nothing at all.
   */
  get thisDeviceUnchecked(): boolean {
    return this.thisDeviceOwnership === "unchecked";
  }

  isOn(leg: PushLeg, kind: string): boolean {
    const set = this.legs[leg];
    return (
      set.includes(kind) ||
      (kind.startsWith("ticket_") && set.includes("ticket_*"))
    );
  }

  /** Any kind on for the leg — the leg's master state, derived, never stored. */
  legOn(leg: PushLeg): boolean {
    return this.legs[leg].length > 0;
  }

  /** Whose settings are in this store. A different person means: start empty. */
  private loadedForUserId: number | null = null;
  /**
   * Last load wins. The reaction starts a load whenever the signed-in person
   * changes, so two can overlap: without this, A's slower answer would land
   * after B's and show B the switches and devices of the person who just
   * signed out — the exact leak the reaction exists to prevent.
   */
  private loadSeq = 0;
  /**
   * Bumped ONLY by `reset()` — i.e. only when this store is emptied because
   * the signed-in person changed. `loadSeq` cannot serve here: it also moves
   * for an ordinary refresh, and gating a write on it would silently swallow a
   * failed save whenever a benign concurrent load happened to overtake it.
   */
  private accountEpoch = 0;
  /**
   * WHAT HAS BEEN WRITTEN BESIDE A LOAD THAT IS STILL IN FLIGHT — the ordering
   * the other two counters do not supply.
   *
   * `loadSeq` orders a load against a load. `accountEpoch` orders a save
   * against a sign-out. NOTHING ordered a load against a SAVE, and `legs` is
   * written by two paths that share no ordering at all: `saveLeg`, from the
   * create answer, and `load`, from the find answer. A find issued before a
   * save's transaction commits, and answering after the save's answer has been
   * applied, put the whole of `legs` back to the instant the find ran —
   * `["ticket_comment"]` on screen over `["ticket_comment","ticket_assigned"]`
   * on the server, with `error` and `actionError` both null. Nothing disables
   * the switches while a load is in flight (the cards disable on
   * `saving[leg]`, which a load never sets, and the held-subscription alerts'
   * own "Check again" button is what starts one), so the person's next click
   * writes that stale view back and DELETES the kind they had just turned on.
   * That is `saveLeg`'s own "told a leg is off while it is being fed, and one
   * click away from writing that stale view to the database" — the same harm,
   * one site over, at the read instead of at the save.
   *
   * THE RULE, and it is the rule the other two counters already state: AN
   * ANSWER MAY ONLY WRITE WHAT NOTHING HAS WRITTEN SINCE IT WAS ASKED FOR. A
   * load captures these when it is issued and, when it answers, writes only
   * the fields whose counter has not moved.
   *
   * PER LEG, because a push save says nothing about gantry: skipping `legs`
   * whole would trade the person's own just-saved switches for the other leg's
   * news, which is the trade `saveLeg` refuses in the other direction.
   *
   * ONCE FOR THE DEVICE, because `thisDeviceOwnership`, `thisDeviceEndpoint`
   * and `confirmedForUserId` are one verdict published together — and it is
   * published from an answer just as `legs` is. `enableThisDevice`,
   * `disableThisDevice` and every drop write that verdict from OUTSIDE a load,
   * and the switch they are pressed on is disabled by `saving.device` and
   * never by `loading`. Un-ordered, a load already in flight republished
   * "unknown" from the endpoint the browser named before any of it — "this
   * browser holds a push subscription HQ could not confirm" over a browser
   * that has just let go of everything.
   *
   * Every OTHER field `load()` writes has no second writer to be ordered
   * against: `kinds`, `pushConfigured`, `publicKey` and `devices` are written
   * by `load()` alone (the device paths that CHANGE what is registered end in
   * a `load()` of their own, which bumps `loadSeq` and retires the older one),
   * and `localEvidenceUnavailable` is measured in line rather than carried in
   * an answer.
   */
  private legWrites: Record<PushLeg, number> = perLeg(() => 0);
  private deviceWrites = 0;

  /**
   * A METHOD, not a getter. `makeAutoObservable` turns a getter into a
   * computed, and the reaction below keeps that computed alive — so once it
   * had been evaluated it would never re-run, because the auth store it reads
   * is not this store's own observable state. It would then report the FIRST
   * signed-in person forever, which is precisely the account-switch bug this
   * exists to catch.
   *
   * `currentUser` first — that is what every other store in this app reads,
   * and `user` is the raw auth payload behind it — and BOTH readings, plus
   * what counts as a person, come from `auth/userId`. This method used to test
   * `typeof id === "number"` in line, which reads the whole restored-session
   * window (the id is the string "5" until the first server round-trip) as
   * "nobody is signed in": user "1", the signed-out `null` and user "2" all
   * arrive here as null, so the reaction below sees no change, never resets,
   * and hands the arriving person the previous one's switches, devices and
   * endpoint — which they can then save to their own account. The same
   * sentence had been written three times in three stores and fixed at one;
   * it is now written once, and `auth/userId` carries the rule and the grep
   * that finds a fourth copy.
   */
  private currentUserId(): number | null {
    return currentUserIdOf(this.rootStore);
  }

  /**
   * Drop everything held for a previous account. Nothing here survives a
   * sign-out: preferences, device list and the browser endpoint are all
   * per-person, and showing one person's devices to the next one on a shared
   * browser is the failure this exists to prevent.
   *
   * WHAT IT LEAVES BEHIND MUST BE A STORE IN WHICH NOTHING IS ASSERTED AND
   * NOTHING IS STUCK, and the second half is as load-bearing as the first. The
   * flags that say something is HAPPENING are cleared here and not by whatever
   * was in flight, because what was in flight cannot do it:
   *
   *   • `load()` clears `loading` only `if (seq === this.loadSeq)`, and the
   *     line below has just bumped that. A sign-out that supersedes a load
   *     starts no successor (the reaction loads only when somebody signs IN),
   *     so `loading` stayed true with nothing left to turn it off — and the
   *     page's own re-entry guard is `!loaded && !loading`, so a remount would
   *     not restart it either, while the button that would ask again is
   *     disabled BY the flag that is stuck.
   *   • `saving` need never settle at all: `enableThisDevice` waits on
   *     `Notification.requestPermission()`, a person deciding, which this
   *     store deliberately puts no deadline on. All three save sites clear
   *     their own slot in a `finally`; none of those runs for a prompt nobody
   *     has answered. Left set, it disables the arriving person's switches
   *     with nothing in flight to re-enable them — so every slot is cleared
   *     here, not just the one that happens to be set.
   *   • `localEvidenceUnavailable` is evidence GATHERED, and the arriving
   *     person's load gathers it again — except in the one shape where it
   *     deliberately does not: a readable-but-empty store leaves the flag
   *     alone ("reads work" is not "writes work"), so a stale `true` from the
   *     session that ended would tell this person their browser cannot
   *     remember whose subscription this is when it demonstrably can.
   *
   * `lastSignedInUserId` is the one thing carried across on purpose — see the
   * field. Everything else must match a store that has just been built, which
   * the suite asserts field-for-field rather than by eye, because this list is
   * hand-written beside a class that grows.
   */
  reset() {
    runInAction(() => {
      // Anything still in flight belonged to the person who left.
      this.loadSeq += 1;
      this.accountEpoch += 1;
      this.legs = perLeg<string[]>(() => []);
      this.kinds = [];
      this.devices = [];
      // "unchecked", not "none": the arriving person's load has not asked this
      // browser anything yet, and until it has, nothing is known. See the field.
      this.thisDeviceOwnership = "unchecked";
      this.thisDeviceEndpoint = null;
      this.confirmedForUserId = undefined;
      this.pushConfigured = null;
      this.emailState = null;
      this.publicKey = null;
      this.loaded = false;
      this.error = null;
      this.actionError = null;
      this.loadedForUserId = null;
      // Nothing is in flight for THIS store any more — see the docblock.
      this.loading = false;
      this.saving = {
        ...perLeg(() => false),
        device: false,
        forget: false,
      };
      this.localEvidenceUnavailable = false;
      // The load-versus-save counters go back to a fresh store's values, unlike
      // the two generation counters above them, and they can: every operation
      // that captured one has just been retired by those two lines — a load by
      // `loadSeq` (it returns before it compares anything), a save by
      // `accountEpoch`. Nothing is left that could read a counter across this
      // point, so there is nothing for a carried value to protect.
      this.legWrites = perLeg(() => 0);
      this.deviceWrites = 0;
    });
  }

  /**
   * Who this browser carried before the current sign-in: this store's own
   * memory first (it is at least as fresh), then the marker persisted by the
   * previous page load.
   */
  private previousUserOnThisBrowser(): number | null {
    return this.lastSignedInUserId ?? readLastSignedInUser();
  }

  /**
   * Let go of the browser's push subscription, and of the local record that
   * describes it — the ONE place in this store that calls `unsubscribeBrowser`.
   *
   * NOT ATOMIC, so it is ordered as though it might fail, because it does:
   * `sub.unsubscribe()` rejects when the push service is unreachable, and
   * `unsubscribeBrowser` first awaits `navigator.serviceWorker.ready`, which
   * on a worker that never activates does not settle at all. Nothing is
   * cleared until it has answered — and it is given a deadline to answer in,
   * after which the silence counts as a refusal, which is the reading that
   * keeps the subscription and the record rather than the one that forgets
   * both.
   *
   *   released → the subscription is gone from the browser, and only now is
   *              the record about it stale.
   *   refused  → the subscription is still HELD and still VALID, so nothing
   *              404s and nothing is pruned server-side either. The record is
   *              KEPT: it is the evidence drop rule (1) in `load()` runs on the
   *              next time somebody signs in on this browser.
   *
   * ONE ENDPOINT, AND IT IS THE ONE THE CALLER DECIDED ABOUT. The deadline
   * above abandons a call; it cannot cancel it, so a release decided in a
   * session that has ended still arrives at the browser. Given no endpoint,
   * `unsubscribeBrowser` dropped whatever the browser was holding at THAT
   * moment — which, on a shared browser, is the subscription the next person
   * has since registered. The effect must act on the thing the decision was
   * about, so the thing it was about is what it is told.
   *
   * The returned `endpoint` is what was actually released — `null` when the
   * endpoint named is no longer on this browser (it holds nothing, or holds
   * another one, which is left alone) — for the caller that must also remove
   * the API row. `clearDeviceOwner` is scoped the same way and for the same
   * reason: the local record about somebody else's endpoint is their local
   * defence, not ours to wipe.
   */
  private async releaseBrowserSubscription(
    endpoint: string,
  ): Promise<ReleaseOutcome> {
    try {
      const released = await withinDeadline(
        unsubscribeBrowser(endpoint),
        BROWSER_ANSWER_DEADLINE_MS,
      );
      clearDeviceOwner(endpoint);
      // THREE ANSWERS OUT, BECAUSE THREE WENT IN. `unsubscribeBrowser` returns
      // `null` for "the endpoint you named is not on this browser" — it holds
      // nothing, or it holds another one and left it alone — and that is NOT
      // the same event as "it let go of the one you named". Mapping it to
      // `released: true` with a null endpoint reintroduced, one layer up, the
      // exact collapse the layer below was rewritten to remove: it skipped the
      // API row removal in `disableThisDevice` (the device came back on the
      // next load with nothing to explain it), and it let
      // `dropUnownedSubscription` publish "this browser holds nothing" over a
      // browser that may be holding somebody else's live subscription.
      return released === null
        ? { kind: "not-held" }
        : { kind: "released", endpoint: released };
    } catch (err) {
      return { kind: "refused", error: err as Error };
    }
  }

  /**
   * DROP A SUBSCRIPTION THIS BROWSER HOLDS THAT IS NOT THE SIGNED-IN PERSON'S.
   *
   * The one way this store does that, used by every site that does it: both
   * drops in `load()` (local evidence names somebody else; the server answered
   * "not yours"), the `!saved` branch of `enableThisDevice()`, and
   * `abandonDeviceRegistration()`. Four copies of this rule is how a fix
   * reached two of them.
   *
   * The state is written FROM the outcome, never before it — a card saying
   * this browser holds nothing must not exist while it is still holding
   * something:
   *   released → "none", and the endpoint is forgotten.
   *   refused  → "stranded", and the endpoint is KEPT and named. Never "none":
   *              that would tell the person this browser holds nothing while
   *              somebody else's push is still arriving on it.
   *
   * `disableThisDevice()` is deliberately NOT one of these callers: there the
   * subscription IS the signed-in person's, so a browser that will not let go
   * of it keeps the switch on and says why — "stranded" there would be the
   * same lie pointing the other way.
   *
   * `stillCurrent` gates the state write ONLY, never the unsubscribe itself
   * (which is attempted whatever else has happened since): a load whose
   * generation has been superseded must not write over the newer one's state.
   *
   * IT IS REQUIRED, with no default. The default was `() => true` — a check
   * that is not a check — and the two callers that took it (`enableThisDevice`
   * and, through it, `abandonDeviceRegistration`) were the two that most needed
   * one: both are reached after awaits with no bound on them at all, a
   * permission prompt a person can leave open and a round trip. An abandoned
   * registration then published "this browser holds nothing" into the session
   * that had replaced it, over the device the arriving person had just turned
   * on. Every site now says which generation its decision belongs to.
   */
  private async dropUnownedSubscription(
    endpoint: string,
    stillCurrent: () => boolean,
  ): Promise<ReleaseOutcome["kind"]> {
    const outcome = await this.releaseBrowserSubscription(endpoint);
    if (stillCurrent())
      runInAction(() => {
        if (outcome.kind === "released") {
          this.thisDeviceOwnership = "none";
          this.thisDeviceEndpoint = null;
        } else if (outcome.kind === "not-held") {
          // THE ENDPOINT THIS DROP WAS ABOUT IS NOT ON THIS BROWSER — and that
          // is all that has been established. It may be holding NOTHING, or it
          // may be holding a different, live subscription that was left alone
          // because no part of this decision was about it. "none" asserts the
          // first over both, which is the sentence this function's own
          // docblock names as the lie it must never tell. What the browser
          // holds now has not been asked, which is what "unchecked" says; the
          // load this sits inside re-asks on its next pass.
          this.thisDeviceOwnership = "unchecked";
          this.thisDeviceEndpoint = null;
        } else {
          this.thisDeviceOwnership = "stranded";
          this.thisDeviceEndpoint = endpoint;
        }
        this.confirmedForUserId = undefined;
        // The device verdict has been written. A load whose find was issued
        // before this line must not publish its own over the top of it — see
        // `deviceWrites`. `load()` re-captures after ITS drops: a drop a load
        // made itself is not a write beside it.
        this.deviceWrites += 1;
      });
    return outcome.kind;
  }

  /**
   * REMEMBER THAT WHAT THIS BROWSER HOLDS IS SETTLED FOR THIS PERSON.
   *
   * `hq.push.last-user` arms drop rule (2) in `load()` — "somebody else was
   * signed in on this browser, and nothing vouches for the subscription it is
   * holding" — and advancing it SPENDS that rule for the account change it
   * describes. So it moves only where this store has actually settled the
   * question, which is two places:
   *
   *   • `load()`, once the browser has been ASKED what it holds and there was
   *     either nothing to drop or the drop succeeded;
   *   • `enableThisDevice()`, once HQ has registered this browser's
   *     subscription to this person — which vouches for it however little the
   *     local record can be kept.
   *
   * Not over a drop that failed — EITHER drop, the local-evidence one or the
   * server's "not yours" (the previous account's subscription is still live on
   * the device) — and not over a load that could not ask the browser at all
   * (nothing was looked at, so nothing was settled). Either would disarm the
   * rule while the case it exists for is still open. `load()` therefore calls
   * this below both drops, not between them.
   */
  private markAccountDealtWith(who: number) {
    this.lastSignedInUserId = who;
    // A refused write costs a drop that would have happened on the next page,
    // never a wrong one — so it is not surfaced. See the field's docblock.
    writeLastSignedInUser(who);
  }

  /**
   * THE ANSWER THIS STORE ALREADY HAS about this browser, normalised for the
   * person now signed in — and the ONE place that says which answers an
   * unanswered question is allowed to leave standing.
   *
   * `unchecked` is not an answer, it is the absence of one ("nothing is known,
   * because this browser has not said"). So it must never be written OVER an
   * answer that was obtained: every other value here was established by
   * something that actually happened —
   *
   *   "stranded" — the browser was asked, it named an endpoint, the drop was
   *                attempted and REFUSED. Still true until something
   *                contradicts it, and the loudest thing this page says.
   *   "unknown"  — the browser named an endpoint and the server did not vouch
   *                for it. Still true.
   *   "none"     — the browser ANSWERED, and held nothing.
   *   "mine"     — the server confirmed it, or this store registered it.
   *
   * `mine` is the one answer that belongs to a PERSON as much as to an
   * endpoint, so it is the one answer that does not survive on its own: it is
   * re-asserted only for the person it was granted to (`confirmedForUserId`,
   * this store's own memory, cleared by `reset()`). Keyed on the endpoint
   * alone it would hand A's green switch to B on the one load where nobody can
   * contradict it. For anybody else it degrades to `unchecked` — refusing to
   * confirm, which is the safe direction, rather than asserting "none", which
   * is not.
   *
   * The other three are not person-keyed because they cannot outlive their
   * person: `reset()` puts this field back to `unchecked` the moment the
   * signed-in person changes, so what is established here was established in
   * THIS person's session. (And of the three, only "none" would be a fail-open
   * if it ever did outlive one — the other two warn.)
   */
  private establishedOwnership(who: number | null): DeviceOwnership {
    if (this.thisDeviceOwnership !== "mine") return this.thisDeviceOwnership;
    return this.confirmedForUserId !== undefined &&
      this.confirmedForUserId === who
      ? "mine"
      : "unchecked";
  }

  /**
   * WHOSE DEVICE THIS IS — written from what the browser said it holds and
   * what the server said about it, and from nothing else.
   *
   * A method, because it is called on every exit of `load()` that publishes
   * anything — the reads having settled however they settled, and the catch
   * that is left for anything else that throws — since this decision depends
   * on neither the preferences read nor on the shape a failure took. It used to
   * be published on the settling exit only, so the
   * same situation reached the person differently depending on that shape: a
   * swallowed read left "this browser holds something HQ could not confirm" on
   * screen, while a read that REJECTED left the store's "none" — a plain Off
   * switch over a live subscription. (`load()` also returns early twice on its
   * generation guard, publishing nothing at all: a superseded load must not
   * write over the state of the one that replaced it.)
   *
   * `thisDeviceId` is the server's answer about the endpoint that was sent: a
   * number is "yours", `undefined` is "not asked, or did not answer" — which
   * is not "not yours", and not "yours" either. Strict null IS "not yours",
   * and is acted on before this is reached, so what arrives here after one is
   * either `stranded` or a browser that no longer holds anything; were one to
   * reach the ladder anyway it falls to "unknown" — or stays "stranded" if
   * that is what was already established of the same endpoint. Either way it
   * refuses to confirm rather than granting anything.
   */
  private publishDeviceOwnership(
    held: BrowserHolding,
    stranded: boolean,
    who: number | null,
    thisDeviceId?: number | null,
  ) {
    // AN UNANSWERED QUESTION NEVER OVERWRITES AN ANSWER THIS STORE ALREADY
    // HAS. It refuses to grant a new one — so a swallowed reload, a field the
    // server left out, or a browser that could not be asked keeps the card as
    // it was, like the form on a failed refresh. `establishedOwnership` is
    // that answer, and the only place the rule is written down.
    const established = this.establishedOwnership(who);
    const keepsConfirmation = established === "mine";
    let ownership: DeviceOwnership;
    if (stranded) {
      // Whatever the server says, this browser is still holding a subscription
      // it just tried and failed to let go of. It is not this person's to
      // switch on, and it is NOT gone.
      ownership = "stranded";
    } else if (held.kind === "unavailable") {
      // THE BROWSER COULD NOT BE ASKED, so this load learned nothing at all —
      // and nothing is exactly what it is entitled to change. "none" here
      // would be the answer to a question nobody got to put; "unchecked" over
      // an answer already obtained is the same mistake one step quieter, and
      // its sentence — "this browser has not said whether it is holding a push
      // subscription" — is FALSE of a browser that said so a moment ago. It
      // was at its worst on the red card's own Try again button: both
      // `unsubscribeBrowser()` and `currentSubscription()` wait on
      // `navigator.serviceWorker.ready`, so the one worker that stranded a
      // subscription is the one that cannot answer the retry, and pressing the
      // button replaced "may still be delivering notifications to another
      // account" with a calm "nothing has been checked".
      ownership = established;
    } else if (held.kind === "none") {
      // The browser ANSWERED, and holds nothing: a real answer, and the only
      // thing entitled to retire a "stranded" — the subscription is gone.
      ownership = "none";
    } else if (typeof thisDeviceId === "number") {
      // The server vouched for the endpoint the browser named. A positive
      // answer, so it supersedes anything established before it — including a
      // "stranded" the person has just taken over with the switch.
      ownership = "mine";
    } else if (keepsConfirmation && this.thisDeviceEndpoint === held.endpoint) {
      ownership = "mine";
    } else if (
      established === "stranded" &&
      this.thisDeviceEndpoint === held.endpoint
    ) {
      // THE SERVER IS THE UNANSWERED ONE THIS TIME, and the same rule holds.
      // The browser still names the endpoint that was established as not this
      // person's and that it refused to let go of; a server that said nothing
      // has not contradicted that. Falling to "unknown" here downgrades a red
      // "not yours, still delivering" to an amber "could not confirm" on the
      // strength of a question nobody answered. A DIFFERENT endpoint is new
      // information and does fall through: the old verdict was about the old
      // one.
      ownership = "stranded";
    } else {
      ownership = "unknown";
    }
    this.thisDeviceOwnership = ownership;
    if (ownership === "none") this.thisDeviceEndpoint = null;
    else if (held.kind === "held") this.thisDeviceEndpoint = held.endpoint;
    // `unavailable`: this load learned nothing about what the browser holds,
    // so what was already known about it stands.
    this.confirmedForUserId = ownership === "mine" ? who : undefined;
  }

  async load() {
    const who = this.currentUserId();
    if (this.loadedForUserId !== null && this.loadedForUserId !== who) this.reset();
    // Whom this browser carried BEFORE this load — read before it is updated,
    // because "somebody else was signed in here earlier" is the drop signal.
    const previousUser = this.previousUserOnThisBrowser();
    const seq = ++this.loadSeq;
    // A sign-out that lands while this load is out ends it: nothing it read,
    // and no failure it met, belongs to whoever is on screen afterwards. The
    // reads below RESOLVE null on a SessionEnded (throwOnError is off), so it is
    // asked here rather than caught; `reset()` also moves `loadSeq`, but only
    // once the purge reaches it, and this answers the moment the epoch moves.
    const ended = sessionMark(this.rootStore);
    // WHAT THIS LOAD IS STILL ENTITLED TO WRITE WHEN IT ANSWERS. Captured as
    // the load is ISSUED, compared as it lands: anything written beside it
    // since is newer than the answer it is carrying. See `legWrites`.
    const legsAtIssue = { ...this.legWrites };
    let deviceAtIssue = this.deviceWrites;
    runInAction(() => {
      this.loading = true;
    });
    // WHAT THE BROWSER IS HOLDING, and whether it could be asked at all.
    // Declared out here because the ownership it decides is published on every
    // exit that publishes anything — the reads having settled, and the catch
    // left for anything else that throws — and it depends on none of them.
    // `unavailable` is the honest starting value: until the question has been
    // put, nothing about this device is known. `thisDeviceOwnership` starts in
    // the state this maps to, for the same reason: what the field holds before
    // this line has run is the whole of what a person can be shown until it
    // does, and `currentSubscription()` can take as long as
    // `navigator.serviceWorker.ready` does.
    let held: BrowserHolding = { kind: "unavailable", reason: "not-asked" };
    // The browser refused to let go of a subscription that is not this
    // person's. Set by either drop site, read by both exits.
    let stranded = false;
    try {
      held = await askBrowserWhatItHolds();
      // LOCAL EVIDENCE FIRST, before any request. deviceOwner.ts remembers
      // which signed-in person this browser's subscription was made under.
      // If that is a DIFFERENT person than the one signed in now, the
      // subscription is dropped right here — not after the server answers,
      // and not only if the server answers. The server round-trip alone
      // could not close the shared-browser case: a transport failure there
      // (or a read the ServiceStore swallowed) left the previous person's
      // endpoint live on this device, receiving their ticket titles, for as
      // long as the next person never opened Settings.
      //
      // TWO WAYS TO KNOW WITHOUT THE SERVER, and a localStorage failure is
      // neither of them. `readDeviceOwner` reports `unavailable` separately
      // from `none` for exactly this reason: an unreadable store is not
      // evidence that the endpoint is unclaimed, and the previous code — which
      // saw both as `null` — quietly lost the whole defence in a private
      // window, on a cleared origin, or in a storage-blocking browser.
      //
      //   1. the record names someone else                → drop
      //   2. no record names the person signed in NOW, and a DIFFERENT person
      //      was last signed in on this browser (in this session, or on the
      //      page before it — the marker is persisted)  → drop
      //
      // (2) is the record-less case: the account demonstrably changed on this
      // browser, and nothing says the held subscription followed it.
      //
      // THE FLOOR, EXACTLY. Both rules need something to read. A browser that
      // refuses storage has neither record nor marker beyond this session's
      // memory, so on the FIRST load of a fresh page there — and for any
      // subscription made before this build shipped, and after any site-data
      // clear that leaves the worker's subscription alive — the only defence
      // left is the server's answer. If that is swallowed too, the
      // subscription is KEPT and reported as unconfirmed. Deliberate:
      // dropping on no evidence would make every private window a push
      // outage. It is a floor, not a closed case.
      //
      // Both rules also need an ENDPOINT: the record is bound to one, and
      // there is nothing to drop without one. So all of this is inside the
      // "the browser answered, and named what it holds" case. A browser that
      // could not be asked is not a browser holding nothing — it is a question
      // that never got put, and nothing below is entitled to act as though it
      // was answered.
      if (held.kind === "held") {
        const evidence = readDeviceOwner(held.endpoint);
        // AND ONLY IF THIS LOAD IS STILL THE CURRENT ONE. Every other write
        // this method makes is ordered against something — the two exits by
        // `seq`, the device verdict by `deviceWrites`, the legs by
        // `legWrites`, `enableThisDevice`'s copy of this very flag by its own
        // `who`/`stillCurrent` pair — and this one was ordered against
        // nothing, sitting after an await whose deadline is ten seconds with
        // the nearest `seq` check below it. `reset()` deliberately clears the
        // flag ("a readable-but-empty store leaves it alone, so a stale `true`
        // from the session that ended would tell this person their browser
        // cannot remember whose subscription this is when it demonstrably
        // can") — and a superseded load put it straight back, over a browser
        // whose storage works, with the advice to turn their device off.
        if (seq === this.loadSeq)
          runInAction(() => {
            // An unreadable store is not an empty one, and the difference is
            // the whole local defence. Say which it is; `none` proves only that
            // reads work, so it leaves the flag alone.
            if (evidence.kind === "unavailable")
              this.localEvidenceUnavailable = true;
            else if (evidence.kind === "owner")
              this.localEvidenceUnavailable = false;
          });
        const namesSomeoneElse =
          evidence.kind === "owner" && who !== null && evidence.userId !== who;
        const unvouchedAfterAccountChange =
          evidence.kind !== "owner" &&
          who !== null &&
          previousUser !== null &&
          previousUser !== who;
        // THE DROP GOES THROUGH `dropUnownedSubscription`, like every other
        // drop in this store: it can fail, and when it does the record is kept
        // (rule 1 needs it next time), `held` is kept (so the server is still
        // asked about the endpoint this browser HOLDS, and can still answer
        // "not yours"), and the honest "stranded" state is written THERE AND
        // THEN — before the reads below, which can fail for the same reason
        // the unsubscribe just did. The consolation the old catch was written
        // on ("the server prunes the row on its first 404/410") is exactly
        // wrong in the case it was written for: a subscription that survived
        // unsubscribe is VALID, so it never 404s.
        if (namesSomeoneElse || unvouchedAfterAccountChange) {
          const drop = await this.dropUnownedSubscription(
            held.endpoint,
            () => seq === this.loadSeq,
          );
          // THREE OUTCOMES, THREE READINGS. `not-held` is the one that used to
          // be folded into `released`: the endpoint this browser named a
          // moment ago is not on it any more, so what it holds NOW is
          // unknown — not "nothing". Read as `none` it published a plain Off
          // switch over whatever took its place.
          if (drop === "released") held = { kind: "none" };
          else if (drop === "not-held")
            held = { kind: "unavailable", reason: "answer-stale" };
          else stranded = true;
          // A drop THIS load made is not a write beside it: `held` and
          // `stranded` above already carry it into the verdict published
          // below, so it must not be the thing that suppresses it.
          deviceAtIssue = this.deviceWrites;
        }
      }
      // TWO REQUESTS TO TWO ENDPOINTS, ASKING TWO QUESTIONS — and each answer
      // is used by whoever is entitled to it, whatever the other one did.
      // `Promise.all` coupled them: a preferences read that REJECTED discarded
      // a subscriptions answer already in hand, including the server's
      // strict-null "this endpoint is not yours" — the one answer that makes
      // this store let go of somebody else's push — while the SAME failure
      // swallowed to null kept it. One request, two failure shapes, two
      // outcomes for the previous account's subscription on this device, and
      // the throwing shape was the one that kept delivering.
      const [prefsRead, subsRead] = await Promise.allSettled([
        ask(() => this.preferencesService?.find({ query: {} })),
        // The endpoint is sent only when the browser NAMED one. `{}` asks
        // about no endpoint, and the answer to it carries no `thisDeviceId` at
        // all — "not asked", which the ownership ladder is careful never to
        // read as "not yours". The endpoint remembered from an earlier load is
        // deliberately not sent in its place: it would put a question about an
        // endpoint nothing has confirmed this browser still holds.
        ask(() =>
          this.subscriptionsService?.find({
            query: held.kind === "held" ? { endpoint: held.endpoint } : {},
          }),
        ),
      ]);
      if (seq !== this.loadSeq || ended()) return;
      const p = (
        prefsRead.status === "fulfilled" ? prefsRead.value : null
      ) as
        // Declared as `unknown` where it is read: what came off the wire has
        // been through no validator, and the fields below are checked rather
        // than assumed. See `asList`.
        | {
            legs?: Partial<Record<PushLeg, unknown>>;
            kinds?: unknown;
            email?: unknown;
          }
        | null
        | undefined;
      const s = (subsRead.status === "fulfilled" ? subsRead.value : null) as
        | {
            configured?: boolean;
            publicKey?: string | null;
            subscriptions?: PushDevice[];
            /** This browser's row id, when the endpoint sent belongs to this user. */
            thisDeviceId?: number | null;
          }
        | null
        | undefined;
      // WHY A READ FAILED, in the two shapes a read can fail in — and only one
      // of them happens in this app.
      //
      //   SWALLOWED (what actually occurs here). `tickets.stores.ts` sets no
      //   `throwOnError` and the library defaults it to false, so every find
      //   failure — transport error, the wrapper's own bounded-call timeout,
      //   a 403 — resolves `null` with the reason recorded on `errorOnFind`.
      //   Read off it exactly as the write paths read `errorOnCreate` and
      //   `errorOnRemove`; without that, a person refused by permissions and a
      //   person on a dead network got the same "could not be loaded".
      //
      //   REJECTED (defensive). Reachable only via a per-call `throwOnError`
      //   or a service double that throws, so nothing in this app produces it
      //   — but it is one `find` option away and costs a line, so it is kept
      //   and takes precedence: if a read did throw, the throw is the reason.
      //
      // Either way both answers above were waited for.
      const rejection = ([prefsRead, subsRead].find(
        (r) => r.status === "rejected",
      ) as PromiseRejectedResult | undefined)?.reason as Error | undefined;
      const swallowed =
        (p == null ? swallowedFindMessage(this.preferencesService) : null) ??
        (s == null ? swallowedFindMessage(this.subscriptionsService) : null);
      // THE SAME RULE THE SAVE PATH KEEPS, at the read that fills the whole
      // form: an answer counts only for the fields it actually carries.
      const legKinds = Object.fromEntries(
        PUSH_LEGS.map((leg) => [leg, asList<string>(p?.legs?.[leg])]),
      ) as Record<PushLeg, string[] | null>;
      const anyLegUnanswered = PUSH_LEGS.some((leg) => !legKinds[leg]);
      const kindOptions = asKindOptions(p?.kinds);
      // THE SAME RULE, AT THE OTHER ANSWER'S OWN TWO FIELDS — which were the
      // one place in this method still outside it.
      //
      // `Boolean(s.configured)` read a field the server left out as the
      // sentence "this HQ has no VAPID keys", which the card prints as fact
      // ("an admin needs to set the VAPID keys. Nothing can be sent until
      // then") and which disables the device switch outright. `pushConfigured`
      // is declared `boolean | null` for exactly this reason — "null until the
      // first load" — and `Boolean()` collapsed the third value away again.
      // Worse on a RELOAD, where it is the `legs` harm in another costume: a
      // `true` already on screen replaced by a fabricated `false`, with `error`
      // null because the read "succeeded".
      //
      // `publicKey` cannot use `null` as its unanswered value: null is a REAL
      // answer here (`push-subscriptions` sends `publicKey: null` whenever
      // `configured` is false). Absence is `undefined`, and it is absence that
      // `?? null` turned into "there is no key" — destroying a good key an
      // earlier load had, so the enable button that worked a moment ago
      // refuses with the same false sentence.
      //
      // Both join the guard below rather than being kept as nulls: an answer
      // that does not carry its own top-level fields is not a partial answer to
      // be salvaged, and a failed read is what the rule says every caller
      // treats absence as.
      const configured =
        typeof s?.configured === "boolean" ? s.configured : null;
      const keyAnswered = s != null && s.publicKey !== undefined;
      // THE SERVER ANSWERED "NOT YOURS" (strict null). The drop is AWAITED
      // here, BEFORE anything is published — not fired after the card has
      // gone up. Published first, the page said "this browser holds nothing"
      // during the whole attempt and, if the unsubscribe then failed,
      // permanently: the plain Off switch over somebody else's live
      // subscription, which is the exact state the "stranded" wording exists
      // to prevent. Awaiting it also keeps an immediate "turn this device on"
      // from registering the endpoint to the new person only to have the
      // in-flight unsubscribe invalidate it, leaving a row that can never be
      // delivered to.
      //
      // Afterwards, either `held` is "none" (it is gone) or `stranded` is set —
      // so the ownership ladder never sees a disowned endpoint.
      if (!stranded && held.kind === "held" && s?.thisDeviceId === null) {
        const drop = await this.dropUnownedSubscription(
          held.endpoint,
          () => seq === this.loadSeq,
        );
        // The same three readings as the local-evidence drop above.
        if (drop === "released") held = { kind: "none" };
        else if (drop === "not-held")
          held = { kind: "unavailable", reason: "answer-stale" };
        else stranded = true;
        deviceAtIssue = this.deviceWrites;
        if (seq !== this.loadSeq) return;
      }
      // THE ACCOUNT CHANGE IS DEALT WITH ONLY ONCE BOTH DROPS HAVE HAD THEIR
      // SAY. Advancing the marker over a failed drop spends rule (2) on an
      // attempt that changed nothing; advancing it over a browser that could
      // not be asked spends it on a check that never happened — either way
      // disarming the rule while the previous account's subscription may still
      // be live on this device.
      //
      // It used to sit ABOVE the reads, which made that promise true of the
      // local-evidence drop only: a server verdict of "not yours" whose
      // unsubscribe then failed had already spent the marker by the time
      // `stranded` was set. There are TWO drops, and the marker is about what
      // happened to the subscription, not about which of them looked at it.
      if (who !== null && !stranded && held.kind !== "unavailable")
        this.markAccountDealtWith(who);
      runInAction(() => {
        // WHOSE DEVICE THIS IS, decided from what the browser holds and the
        // subscriptions answer ALONE. The preferences read is a separate
        // request: letting its failure skip this would leave the previous
        // account's push live on this browser until some later load happened
        // to succeed. The same reason it is published on the failing exit
        // below — see `publishDeviceOwnership`.
        //
        // Unless something has written that verdict since this load was
        // ISSUED, in which case the writer looked at the browser later than
        // this answer did and its word stands — see `deviceWrites`.
        if (deviceAtIssue === this.deviceWrites)
          this.publishDeviceOwnership(held, stranded, who, s?.thisDeviceId);
        if (
          !p ||
          !s ||
          anyLegUnanswered ||
          !kindOptions ||
          configured === null ||
          !keyAnswered
        ) {
          // A FIRST read that fails must not render as "everything is off":
          // the switches would look editable, and turning the master switch on
          // would send the empty default and DELETE the kinds actually stored.
          // `loaded` stays false and the page shows the failure.
          //
          // A RELOAD that fails is different — the form on screen is real, and
          // was true a moment ago. Keep it and say the refresh failed, rather
          // than blanking the page over a network blip after a good save.
          this.error =
            rejection?.message ||
            swallowed ||
            (this.loaded
              ? "The settings on screen could not be refreshed; they may be out of date."
              : "Notification settings could not be loaded.");
          // Only a form that never loaded is taken away; one on screen was
          // true a moment ago and stays, over the failure.
          if (this.loadedForUserId === null) this.loaded = false;
          this.loading = false;
          return;
        }
        // PER LEG, AND ONLY WHERE NOTHING HAS BEEN SAVED SINCE. A leg whose
        // save committed after this find was issued keeps what the save put
        // there — the answer in hand describes the server as it was BEFORE
        // that write. The other leg still refreshes: it is the only way news
        // from another tab or another device arrives. See `legWrites`.
        this.legs = Object.fromEntries(
          PUSH_LEGS.map((leg) => [
            leg,
            this.legWrites[leg] === legsAtIssue[leg]
              ? (legKinds[leg] as string[])
              : this.legs[leg],
          ]),
        ) as Record<PushLeg, string[]>;
        this.kinds = kindOptions;
        this.pushConfigured = configured;
        // Only an answer replaces what is on screen; see the field.
        const emailAnswer = asEmailDeliveryState(p?.email);
        if (emailAnswer) this.emailState = emailAnswer;
        this.publicKey = s.publicKey ?? null;
        // `?? []` survives here on purpose, and it is the one place it does:
        // nothing is ever WRITTEN from `devices`. It is a list to look at, and
        // `forgetDevice` acts on an id taken from a row that is on it — so a
        // list the server left out can only offer fewer actions than it should,
        // never a wrong one. `legs` and `kinds` above are the opposite: what
        // they show is what the next click saves.
        this.devices = s.subscriptions ?? [];
        this.error = null;
        this.loaded = true;
        this.loadedForUserId = who;
      });
      // The local record follows the server's verdict: confirmed → remembered
      // for this person; disowned → forgotten above, together with the
      // subscription, and ONLY if the browser actually let go of it — when it
      // did not, the record is what arms drop rule (1) for the next sign-in.
      // Re-read the signed-in person: stamping the endpoint with an id that is
      // no longer signed in is exactly the poisoned record that would suppress
      // the drop rules above on the next load.
      // Only an endpoint this load actually READ off the browser is recorded:
      // a load that could not ask has no endpoint to stamp, and the record
      // that granted the confirmation it is re-asserting was written when it
      // was granted.
      if (
        deviceAtIssue === this.deviceWrites &&
        held.kind === "held" &&
        this.thisDeviceOwnership === "mine" &&
        who !== null &&
        this.currentUserId() === who
      ) {
        // The boolean is the point of the write: a browser that refused the
        // record is a browser with no local defence for this endpoint, and the
        // page says so rather than letting the caller assume the best.
        const landed = writeDeviceOwner(held.endpoint, who);
        runInAction(() => {
          this.localEvidenceUnavailable = !landed;
        });
      }
    } catch (err) {
      if (seq !== this.loadSeq || isSessionEnded(err) || ended()) return;
      runInAction(() => {
        // THE SAME OWNERSHIP THE OTHER EXIT PUBLISHES. What this browser is
        // holding was decided before the reads and does not depend on them, or
        // on how they failed — but it was published on one exit only, so the
        // person got "HQ could not confirm this device" for a swallowed read
        // and a plain Off switch, over the very same live subscription, for a
        // read that rejected. A rejecting read no longer arrives here at all
        // (`allSettled` keeps it in the exit above); this catch is what is left
        // for anything else in this method that throws, and it must publish
        // the same state rather than leave whatever the load started with.
        if (deviceAtIssue === this.deviceWrites)
          this.publishDeviceOwnership(held, stranded, who);
        this.error =
          (err as Error)?.message ||
          "Notification settings could not be loaded.";
        // Same rule: only a form that never loaded is taken away.
        if (this.loadedForUserId === null) this.loaded = false;
      });
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq) this.loading = false;
      });
    }
  }

  /** Clear the last action failure (the Alert's dismiss button). */
  dismissActionError() {
    this.actionError = null;
  }

  /** Replace one leg's list on the server (the API contract is whole-list). */
  private async saveLeg(leg: PushLeg, kinds: string[]) {
    const service = this.preferencesService;
    if (!service) return;
    // The same discipline every read in this store keeps, and the one place
    // that was outside it: a save is a round trip, and its answer describes
    // whoever asked for it. If the signed-in person changed while it was in
    // flight — or the store was emptied for a change of person and back again
    // — the switches it carries belong to a session that ended. Assigning them
    // would show the person on screen the previous account's opt-ins, and an
    // error from it would be their error to answer for. Neither is true, so
    // both are dropped.
    const who = this.currentUserId();
    const epoch = this.accountEpoch;
    // ...and the session itself: a SessionEnded save RESOLVES null here (the
    // ServiceStore swallows it), which would otherwise read as a refusal — an
    // error for the next person, and a reload started in THEIR session.
    const ended = sessionMark(this.rootStore);
    const stale = () =>
      epoch !== this.accountEpoch || this.currentUserId() !== who || ended();
    let releaseSaving: () => void = () => {};
    runInAction(() => {
      releaseSaving = this.claimSaving(leg);
      this.actionError = null;
    });
    try {
      const res = (await service.create({ leg, kinds })) as unknown as
        | { legs?: Partial<Record<PushLeg, unknown>> }
        | null
        | undefined;
      if (stale()) return;
      const saved = asList<string>(res?.legs?.[leg]);
      // AN ANSWER THAT DOES NOT CARRY THIS LEG IS NOT AN ANSWER ABOUT IT.
      // `?? []` here reported every kind on the leg just written as off, which
      // the person's next click would then save for real. Absence is treated
      // exactly like the swallowed failure below: say the server is unchanged
      // and go and read what it actually holds.
      if (!saved) {
        // The ServiceStore swallowed a failure: the server did NOT change. Say
        // so and re-read the truth rather than showing the switch the person
        // wanted — a failed "off" that reads as off keeps pushing.
        const err = (
          service as unknown as { errorOnCreate?: { message?: string } | null }
        ).errorOnCreate;
        runInAction(() => {
          this.actionError =
            err?.message ||
            "The switch could not be saved; it is unchanged on the server.";
        });
        await this.load();
        return;
      }
      // ONE LEG'S ANSWER UPDATES ONE LEG.
      //
      // `notification-preferences.create` ends in `return this.find(params)`
      // (api ~:71), so what comes back is a WHOLE-STORE snapshot taken when
      // THIS write committed — the other leg is in it only incidentally, and
      // only as it stood at that instant. Assigning both legs from it put the
      // other leg back to that instant: a person who toggled a push kind (slow)
      // and then a gantry kind (fast) ended with the server holding both and
      // the screen showing gantry off, with `error` and `actionError` both null
      // — told a leg is off while it is being fed, and one click away from
      // writing that stale view to the database.
      //
      // The other leg's half of the snapshot is not WRONG, only possibly older
      // than what is on screen, and nothing in the payload says which. Dropping
      // it can therefore only lose a change made elsewhere (another tab, another
      // device) between this leg's two reads — which the next `load()` supplies
      // anyway, and which the person's own concurrent click cannot produce.
      // Keeping it can lose the person's own just-saved switches, silently. So
      // the leg that was asked about is the leg the answer is allowed to touch.
      runInAction(() => {
        this.legs = { ...this.legs, [leg]: saved };
        // This leg has been written. A load whose find was issued before this
        // line may no longer overwrite it — see `legWrites`.
        this.legWrites[leg] += 1;
      });
    } catch (err) {
      if (isSessionEnded(err) || stale()) return;
      runInAction(() => {
        this.actionError =
          (err as Error)?.message || "The switch could not be saved.";
      });
    } finally {
      // THIS leg's flag, and only this leg's: a save that settles says nothing
      // about the one still in flight beside it. Cleared even for a stale
      // answer — the answer is discarded, but a flag left set would disable the
      // arriving person's switches with nothing in flight to re-enable them.
      // This is the local half only: it runs when THIS save settles, and a save
      // need not settle at all (the permission prompt in `enableThisDevice`).
      // `reset()` is what closes the hazard for all three save sites.
      //
      // AND ONLY IF THIS SAVE STILL OWNS THE SLOT. "Cleared even for a stale
      // answer" is right, and clearing a flag a LATER press of the same leg
      // set is not: that re-enables the switch over a save still in the air.
      // See `savingSeq`.
      runInAction(() => {
        releaseSaving();
      });
    }
  }

  /**
   * The kinds THIS leg may carry — the only ones its card draws, and the only
   * ones its master switch is allowed to post.
   *
   * `notification-preferences` refuses the whole write if any kind in it cannot
   * ride the leg, so "every kind" and "every kind on this leg" are not a
   * cosmetic difference: the first is a 400 that saves nothing.
   */
  kindsFor(leg: PushLeg): PushKindOption[] {
    return this.kinds.filter((k) => k.legs.includes(leg));
  }

  async toggleKind(leg: PushLeg, kind: string, on: boolean) {
    const current = new Set(this.legs[leg]);
    // Expand the family switch into its members before editing one of them —
    // this leg's members, since they are what this leg's list may contain.
    if (current.has("ticket_*") && kind.startsWith("ticket_")) {
      current.delete("ticket_*");
      for (const k of this.kindsFor(leg).map((k) => k.kind))
        if (k.startsWith("ticket_")) current.add(k);
    }
    if (on) current.add(kind);
    else current.delete(kind);
    await this.saveLeg(leg, [...current]);
  }

  /** The leg's master switch: on = every kind THIS LEG CARRIES, off = none. */
  async setLeg(leg: PushLeg, on: boolean) {
    await this.saveLeg(leg, on ? this.kindsFor(leg).map((k) => k.kind) : []);
  }

  /**
   * The signed-in person changed while this device was being registered.
   *
   * Both awaits in `enableThisDevice` are unbounded: `subscribeBrowser` opens
   * `Notification.requestPermission()`, a prompt a person can leave sitting
   * for as long as they like, and `create()` is a round trip. The subscription
   * that comes back was made under the account that asked for it and belongs
   * to nobody now — so drop it in the browser and, above all, write NO local
   * record. Stamping this endpoint with the NEW person's id would be a
   * poisoned record: on the next load it reads as "this browser's subscription
   * is yours" and suppresses the very unsubscribe that exists for the case
   * where the server cannot be reached.
   *
   * The drop goes through the one drop, so it is allowed to fail: "nothing was
   * turned on" is true of HQ's row and false of the browser, which is still
   * holding the subscription. Both sentences below are written from what
   * actually happened to it.
   *
   * AND IT IS SAID TO WHOEVER THE REGISTRATION BELONGED TO, or to nobody. This
   * is the one path in the store that runs BECAUSE the signed-in person
   * changed, so the `currentUserId() !== who` half of the staleness check its
   * siblings keep is always true here and cannot be the gate. `accountEpoch`
   * is — the counter that moves only when `reset()` empties this store for
   * somebody else. When it has moved, the person on screen is not the person
   * this sentence is about: their own device may be on, registered and correct,
   * and telling them "nothing was turned on" over it is the same wrong report
   * the state write beside it makes.
   */
  private async abandonDeviceRegistration(
    endpoint: string,
    stillCurrent: () => boolean,
  ) {
    const drop = await this.dropUnownedSubscription(endpoint, stillCurrent);
    if (!stillCurrent()) return;
    // For the SENTENCE, "released" and "not-held" are the same news: the
    // subscription this registration made is not on this browser. Only
    // "refused" is the case where it is still here, still valid and still
    // delivering to the account that has just left.
    const gone = drop !== "refused";
    runInAction(() => {
      this.actionError = gone
        ? "The signed-in account changed while this device was being registered, so nothing was turned on. Try again."
        : "The signed-in account changed while this device was being registered, and this browser would not let go of the subscription it had made — it may still be delivering notifications to the previous account. Try turning this device off.";
    });
  }

  /** Register THIS browser for push: permission, browser subscription, then the API row. */
  async enableThisDevice() {
    const service = this.subscriptionsService;
    if (!service) return;
    if (!this.publicKey) {
      runInAction(() => {
        this.actionError =
          "Push is not configured on this HQ yet (no VAPID keys). Ask an admin to set PUSH_VAPID_PUBLIC_KEY and PUSH_VAPID_PRIVATE_KEY.";
      });
      return;
    }
    // WHO THIS IS BEING REGISTERED FOR, captured before the permission prompt
    // — not after it and the create() round trip, which is when the endpoint
    // used to be stamped with whoever happened to be signed in by then.
    const who = this.currentUserId();
    // ...AND WHICH SESSION IT BELONGS TO. Neither await below is bounded by
    // anything: the permission prompt is a person deciding, and `create()` is a
    // round trip. Both outlive a sign-out, and everything this method writes
    // afterwards — the drops, their sentences, the failure in the catch — was
    // decided for the account that opened them. `accountEpoch` moves only when
    // `reset()` empties this store because the signed-in person changed, which
    // is exactly the question being asked; `loadSeq` also moves for an ordinary
    // refresh and `currentUserId()` is the condition that TRIGGERS the
    // abandonment, so neither can serve. See `saveLeg` for the same rule where
    // the person-check is available too.
    const epoch = this.accountEpoch;
    const stillCurrent = () => epoch === this.accountEpoch;
    const ended = sessionMark(this.rootStore);
    let releaseSaving: () => void = () => {};
    runInAction(() => {
      releaseSaving = this.claimSaving("device");
      this.actionError = null;
    });
    try {
      const sub = await subscribeBrowser(this.publicKey);
      if (this.currentUserId() !== who) {
        await this.abandonDeviceRegistration(sub.endpoint, stillCurrent);
        return;
      }
      const userAgent =
        typeof navigator !== "undefined"
          ? navigator.userAgent.slice(0, 500)
          : null;
      const saved = await service.create({ subscription: sub, userAgent });
      // THE SIGN-OUT ENDED THE CREATE, which RESOLVES null here (the
      // ServiceStore swallows it) — and is not a refusal. The server performed
      // it (the guard drops only the answer), so the subscription this browser
      // holds IS registered, to the account that left. Dropping it below would
      // unsubscribe a live registration and strand its row; leaving it is what
      // every other sign-out does with a device that is on, and the next
      // sign-in's load drops it (rule 2: the account changed on this browser).
      if (ended() || swallowedSessionEnded(service, "errorOnCreate")) return;
      if (!saved) {
        // Refused or failed server-side (an endpoint HQ does not accept, a
        // transport error): the browser holds a subscription HQ does not know.
        // Drop it again — through the one drop, because this one can fail like
        // any other, and `subscribeBrowser` hands back a subscription the
        // browser ALREADY held when there is one, which may be somebody else's.
        // The switch then tells the truth about what happened to it.
        const err = (
          service as unknown as { errorOnCreate?: { message?: string } | null }
        ).errorOnCreate;
        const refusal =
          err?.message || "HQ did not accept this device's subscription.";
        // The same gate as the abandonment below, and for the same reason: the
        // create can fail BECAUSE the session ended under it, and this refusal
        // is then the departed account's, not the arriving person's.
        const drop = await this.dropUnownedSubscription(
          sub.endpoint,
          stillCurrent,
        );
        if (!stillCurrent()) return;
        // Same reading as `abandonDeviceRegistration`: only a REFUSED drop
        // leaves the subscription on this browser.
        const gone = drop !== "refused";
        runInAction(() => {
          this.actionError = gone
            ? refusal
            : `${refusal} This browser would not let go of the subscription either, so it may still be delivering notifications to another account.`;
        });
        return;
      }
      // The round trip is another window the person can change in.
      if (this.currentUserId() !== who) {
        await this.abandonDeviceRegistration(sub.endpoint, stillCurrent);
        return;
      }
      // Registered to THIS person, by this browser: the local evidence the
      // next sign-in reads before asking anyone. Whether it actually landed is
      // the caller's business — a storage that refused it leaves this endpoint
      // with no local defence at all, which the page states plainly instead of
      // implying the record exists.
      //
      // HQ has just vouched for what this browser holds, so any account change
      // still armed against it is dealt with — including on a browser that
      // cannot keep the record above, where the `load()` below would otherwise
      // find no vouching evidence and drop the subscription this person just
      // asked for.
      const landed = who !== null ? writeDeviceOwner(sub.endpoint, who) : false;
      if (who !== null) this.markAccountDealtWith(who);
      runInAction(() => {
        this.thisDeviceOwnership = "mine";
        this.thisDeviceEndpoint = sub.endpoint;
        this.confirmedForUserId = who;
        if (who !== null) this.localEvidenceUnavailable = !landed;
        // Every device-state write made outside a load bumps this, without
        // exception — the `load()` below retires the older ones by `loadSeq`
        // too, and the invariant is worth more than the one it duplicates.
        this.deviceWrites += 1;
      });
      await this.load();
    } catch (err) {
      // The same gate again: a subscribe or a create that threw after the store
      // was emptied for somebody else threw at the account that left, and its
      // sentence belongs to nobody who is here.
      if (isSessionEnded(err) || ended() || !stillCurrent()) return;
      runInAction(() => {
        this.actionError =
          (err as Error)?.message || "This device could not be subscribed.";
      });
    } finally {
      // Not gated on staleness: a flag left set disables the arriving person's
      // switch with nothing in flight to re-enable it. `reset()` clears it too,
      // because this `finally` need never run at all — the permission prompt is
      // a person deciding, on no deadline. But it IS gated on still owning the
      // slot: this registration is the one most likely to be superseded (both
      // its awaits are unbounded), and `disableThisDevice` claims the very same
      // slot. See `savingSeq`.
      runInAction(() => {
        releaseSaving();
      });
    }
  }

  async disableThisDevice() {
    const service = this.subscriptionsService;
    if (!service) return;
    // WHICH SUBSCRIPTION THIS PRESS IS ABOUT — read before the first await, and
    // the only one this method is allowed to act on. The switch is on only for
    // "mine", which is never published without the endpoint it was granted for,
    // so it always carries one. If it somehow did not there would be nothing
    // this press was decided about, and dropping "whatever the browser holds
    // now" is precisely the effect this store no longer makes.
    const decided = this.thisDeviceEndpoint;
    if (!decided) {
      runInAction(() => {
        this.actionError =
          "HQ does not know which subscription this browser is holding, so there is nothing it can safely turn off. Check again first.";
      });
      return;
    }
    // The same discipline `saveLeg` and `enableThisDevice` keep, and the two
    // sites that were outside it: an answer describes whoever asked for it. If
    // the signed-in person changed while this was in flight — or the store was
    // emptied for a change of person and back again — a failure raised by the
    // session that ended is not the arriving person's error to answer for, and
    // the ownership it decides is about a page nobody is looking at any more.
    // The unsubscribe itself still happens; only what is WRITTEN is gated.
    const who = this.currentUserId();
    const epoch = this.accountEpoch;
    // A remove the sign-out ended RESOLVES null (swallowed), which reads as a
    // failed remove: an error for the next person, and a reload in THEIR session.
    const ended = sessionMark(this.rootStore);
    const stale = () =>
      epoch !== this.accountEpoch || this.currentUserId() !== who || ended();
    let releaseSaving: () => void = () => {};
    runInAction(() => {
      releaseSaving = this.claimSaving("device");
      this.actionError = null;
    });
    try {
      // The same release every drop in this store uses — but NOT the defensive
      // drop: this subscription IS the signed-in person's. So when the browser
      // refuses to let go, the switch stays ON and says why; reporting it as
      // off would be the same lie as a "stranded" card claiming "none". HQ's
      // row is left alone in that case too, so pressing the switch again does
      // this whole thing again rather than half of it: unregistering a
      // subscription this browser is still holding would only take the device
      // off the list while it is still live. (The reverse is not symmetric —
      // the browser can let go and the row removal still fail, which is what
      // the message further down is for.)
      const outcome = await this.releaseBrowserSubscription(decided);
      if (stale()) return;
      if (outcome.kind === "refused") {
        runInAction(() => {
          this.actionError =
            outcome.error?.message || "This device could not be unsubscribed.";
        });
        return;
      }
      // THE ROW GOES WHETHER THE BROWSER RELEASED IT OR WAS NO LONGER HOLDING
      // IT, and it is keyed on the endpoint this press was DECIDED about.
      //
      // The two are one outcome here: either way the subscription this switch
      // is about is not on this browser any more, and HQ's row for it is a
      // registration for a subscription that is gone. Reading `not-held` as
      // "released, with no endpoint to remove" skipped this block outright —
      // no `remove`, no `actionError`, the switch went Off, and the `load()`
      // below re-listed the very same device under "Devices subscribed on your
      // account" with nothing anywhere to explain it. HQ then goes on pushing
      // to a row nobody can turn off. (`released` names the endpoint back and
      // it is always `decided` — `unsubscribeBrowser` returns it only when it
      // matches — so one key serves both.)
      {
        const endpoint = decided;
        // A SWALLOWED remove is not the same outcome as a missing row. The
        // ServiceStore records the failure in `errorOnRemove` and RESOLVES to
        // null rather than throwing, so catching only a throw left the API row
        // alive — and the next load re-lists it under "Devices subscribed on
        // your account" with no explanation, which is precisely the confusion
        // `forgetDevice` goes out of its way to prevent.
        //
        // A row that was ALREADY gone is the one failure that is not one: see
        // `isAlreadyGone`. The comment here used to claim an empty result meant
        // that, which is dead against this API — it throws `NotFound` and never
        // returns `[]` — so the person whose row had already been pruned was
        // told HQ could not remove a registration that no longer existed.
        let removed: unknown = null;
        let thrown: Error | null = null;
        try {
          removed = await service.remove(null, { query: { endpoint } });
        } catch (err) {
          thrown = err as Error;
        }
        if (stale() || isSessionEnded(thrown)) return;
        if (removed == null || removed === false) {
          const err = (
            service as unknown as {
              errorOnRemove?:
                | { message?: string; name?: string; code?: number }
                | null;
            }
          ).errorOnRemove;
          const failure = thrown ?? err;
          if (!isAlreadyGone(failure))
            runInAction(() => {
              this.actionError =
                thrown?.message ||
                err?.message ||
                "This browser will no longer receive push, but HQ could not remove its registration — it may still be listed below.";
            });
        }
      }
      runInAction(() => {
        // "none" is earned only by a browser that actually let go: it ANSWERED,
        // and now holds nothing. A `not-held` browser answered a different
        // question — the endpoint named is not here — and may be holding
        // something else entirely, so what it holds is unchecked until the
        // `load()` below asks it again.
        this.thisDeviceOwnership =
          outcome.kind === "released" ? "none" : "unchecked";
        this.thisDeviceEndpoint = null;
        this.confirmedForUserId = undefined;
        this.deviceWrites += 1;
      });
      await this.load();
    } catch (err) {
      if (isSessionEnded(err) || stale()) return;
      runInAction(() => {
        this.actionError =
          (err as Error)?.message || "This device could not be unsubscribed.";
      });
    } finally {
      // Only if this press still owns the slot — `enableThisDevice` claims the
      // same one, and either can outlive the other. See `savingSeq`.
      runInAction(() => {
        releaseSaving();
      });
    }
  }

  /** Forget another device from the list (a lost phone). */
  async forgetDevice(id: number) {
    const service = this.subscriptionsService;
    if (!service) return;
    // The sibling of `disableThisDevice`, and it keeps the same two rules: an
    // answer belongs to whoever asked for it, and a row that is already gone is
    // the outcome rather than a failure to reach it.
    const who = this.currentUserId();
    const epoch = this.accountEpoch;
    // A remove the sign-out ended RESOLVES null (swallowed), which reads as a
    // failed remove: an error for the next person, and a reload in THEIR session.
    const ended = sessionMark(this.rootStore);
    const stale = () =>
      epoch !== this.accountEpoch || this.currentUserId() !== who || ended();
    // ...AND IT SAYS SO WHILE IT RUNS, like every other operation on this page.
    // This one announced nothing: a remove round trip plus the `load()` below,
    // with every bin live over a list that reload was about to replace. See the
    // `saving` field.
    let releaseSaving: () => void = () => {};
    runInAction(() => {
      releaseSaving = this.claimSaving("forget");
      this.actionError = null;
    });
    let removed: unknown = null;
    let thrown: Error | null = null;
    try {
      removed = await service.remove(id);
    } catch (err) {
      thrown = err as Error;
    }
    // Every exit from here on goes through the `finally` below, so the flag
    // cannot be left set by the early return the staleness check makes.
    try {
      if (stale() || isSessionEnded(thrown)) return;
      if (removed == null || removed === false) {
        // The ServiceStore swallowed the failure: the device is STILL
        // registered and still receiving. Say so instead of re-listing it with
        // no explanation — unless the row was already gone, which is what a
        // second press of the same bin, another tab, or HQ's own 404/410 sweep
        // looks like, and which the reload below simply reflects.
        const err = (
          service as unknown as {
            errorOnRemove?:
              | { message?: string; name?: string; code?: number }
              | null;
          }
        ).errorOnRemove;
        const failure = thrown ?? err;
        if (!isAlreadyGone(failure))
          runInAction(() => {
            this.actionError =
              thrown?.message ||
              err?.message ||
              "The device could not be removed; it is still registered.";
          });
      }
      // The list is re-read whichever way the remove went: it is the only thing
      // that says what is registered now. (The old catch skipped it, so a throw
      // left the stale list on screen under the error explaining it.)
      await this.load();
    } finally {
      // This operation's own slot, cleared whichever way it went — and, like
      // the other three, not gated on staleness: a flag left set disables the
      // bins with nothing in flight to re-enable them. `reset()` clears it too.
      // Gated on OWNERSHIP, though: a second bin pressed after this one claims
      // the same slot, and this cleanup is not entitled to speak for it. See
      // `savingSeq`.
      runInAction(() => {
        releaseSaving();
      });
    }
  }
}

export default NotificationSettingsStore;
